import {
  ALL_PERMISSIONS,
  normalizePermissionRole,
  rolePermissions,
} from "@/consts/permissions"
import { create } from "zustand"

interface AuthUser {
  id: string
  username: string
  permissions: string[]
}

interface AuthStore {
  isAuthenticated: boolean
  user: AuthUser | null
  accessToken: string | null
  role: string | null
  isImpersonating: boolean
  originalRole: string | null

  // Actions
  setAuthenticated: (
    authenticated: boolean,
    user?: AuthUser,
    accessToken?: string,
    role?: string,
  ) => void
  clearAuth: () => void
  initializeAuth: () => void
  hasPermission: (permission: (typeof ALL_PERMISSIONS)[number]) => boolean
  setImpersonation: (
    impersonating: boolean,
    originalRole?: string | null,
  ) => void
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  isAuthenticated: false,
  user: null,
  accessToken: null,
  role: null,
  isImpersonating: false,
  originalRole: null,

  setAuthenticated: (authenticated, user, accessToken, role) =>
    set({
      isAuthenticated: authenticated,
      user: user || null,
      accessToken: accessToken || null,
      role: role || null,
    }),

  clearAuth: () =>
    set({
      isAuthenticated: false,
      user: null,
      accessToken: null,
      role: null,
      isImpersonating: false,
      originalRole: null,
    }),

  initializeAuth: () => {
    // Initialize auth state from localStorage
    const token = localStorage.getItem("access_token")
    const userStr = localStorage.getItem("auth_user")
    const role = localStorage.getItem("role")
    const originalToken = localStorage.getItem("original_access_token")
    const originalRole = localStorage.getItem("original_role")

    if (token) {
      let user: AuthUser | null = null

      if (userStr) {
        try {
          user = JSON.parse(userStr)
        } catch {
          // Invalid user data, clear it
          localStorage.removeItem("auth_user")
        }
      }

      set({
        isAuthenticated: true,
        user,
        accessToken: token,
        role,
        isImpersonating: Boolean(originalToken),
        originalRole: originalRole,
      })
    } else {
      // No token, ensure we're in unauthenticated state
      get().clearAuth()
    }
  },

  hasPermission: (permission: (typeof ALL_PERMISSIONS)[number]) => {
    const { role } = get()

    if (!role) return false

    return rolePermissions[normalizePermissionRole(role)].includes(permission)
  },

  setImpersonation: (impersonating, originalRole) =>
    set({
      isImpersonating: impersonating,
      originalRole: originalRole ?? null,
    }),
}))
